import { ChevronLeft, Lightbulb, ThumbsUp, Send, Plus, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface FeatureRequest {
    id: string;
    title: string;
    description: string;
    votes: number;
    status: 'planned' | 'in-progress' | 'completed' | 'considering';
}

const popularRequests: FeatureRequest[] = [
    {
        id: '1',
        title: 'Apple Calendar Integration',
        description: 'Sync with Apple Calendar in addition to Google Calendar',
        votes: 234,
        status: 'planned',
    },
    {
        id: '2',
        title: 'Recurring Tasks',
        description: 'Set tasks to repeat daily, weekly, or custom intervals',
        votes: 189,
        status: 'in-progress',
    },
    {
        id: '3',
        title: 'Desktop App',
        description: 'Native apps for Windows and Mac',
        votes: 156,
        status: 'considering',
    },
    {
        id: '4',
        title: 'Team Collaboration',
        description: 'Share tasks and projects with team members',
        votes: 142,
        status: 'planned',
    },
    {
        id: '5',
        title: 'Offline Mode',
        description: 'Use the app without internet connection',
        votes: 128,
        status: 'completed',
    },
];

const statusConfig = {
    planned: { label: 'Planned', color: 'bg-[#D9EEF2] text-[#0D5F6B]', icon: Clock },
    'in-progress': { label: 'In Progress', color: 'bg-[#FFF3E0] text-[#E65100]', icon: TrendingUp },
    completed: { label: 'Shipped', color: 'bg-[#D5F2EA] text-[#3DA58A]', icon: CheckCircle },
    considering: { label: 'Considering', color: 'bg-[#F5F8FA] text-[#7B8C9B]', icon: Lightbulb },
};

export default function AppFeatureRequests() {
    const navigate = useNavigate();
    const [showSubmitForm, setShowSubmitForm] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Send feature request via email
        const emailSubject = encodeURIComponent(`[FEATURE REQUEST] ${title}`);
        const emailBody = encodeURIComponent(`Feature Request:\n\n${title}\n\nDescription:\n${description}`);
        window.location.href = `mailto:support@way2will.com?subject=${emailSubject}&body=${emailBody}`;

        setSubmitted(true);
        setShowSubmitForm(false);
        setTitle('');
        setDescription('');
    };

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Feature Requests</h1>
            </div>

            {/* Submit Success Message */}
            {submitted && (
                <div className="bg-[#D5F2EA] rounded-xl p-4 mb-6 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3DA58A]" />
                    <p className="text-sm text-[#3DA58A] font-medium">
                        Your feature request is being submitted via email!
                    </p>
                </div>
            )}

            {/* CTA Banner */}
            <div className="bg-gradient-to-r from-[#0D5F6B] to-[#2A7A85] rounded-2xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Lightbulb className="w-6 h-6 text-white" />
                    <span className="text-lg font-semibold text-white">Have an idea?</span>
                </div>
                <p className="text-white/80 text-sm mb-3">
                    We love hearing from our users! Submit your feature ideas and help shape the future of Way2Will.
                </p>
                <button
                    onClick={() => setShowSubmitForm(true)}
                    className="px-4 py-2 bg-white text-[#0D5F6B] rounded-xl font-semibold text-sm flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Submit a Request
                </button>
            </div>

            {/* Submit Form Modal */}
            {showSubmitForm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
                    <div className="bg-white dark:bg-dark-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6">
                        <h2 className="text-lg font-bold text-[#1A2832] dark:text-white mb-4">
                            Submit Feature Request
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-[#1A2832] dark:text-white mb-2">
                                    Feature Title
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g., Dark mode for calendar"
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white placeholder-[#7B8C9B] focus:outline-none focus:border-[#0D5F6B]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[#1A2832] dark:text-white mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Describe the feature and how it would help you..."
                                    required
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white placeholder-[#7B8C9B] focus:outline-none focus:border-[#0D5F6B] resize-none"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowSubmitForm(false)}
                                    className="flex-1 py-3 border border-[#DCE4EA] text-[#4A5C6A] rounded-xl font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-3 bg-[#0D5F6B] text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                                >
                                    <Send className="w-4 h-4" />
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Popular Requests */}
            <h2 className="text-base font-semibold text-[#1A2832] dark:text-white mb-3">
                Popular Requests
            </h2>
            <div className="space-y-3">
                {popularRequests.map((request) => {
                    const status = statusConfig[request.status];
                    const StatusIcon = status.icon;
                    return (
                        <div
                            key={request.id}
                            className="bg-white dark:bg-dark-card rounded-xl p-4 shadow-sm"
                        >
                            <div className="flex items-start justify-between mb-2">
                                <h3 className="font-semibold text-[#1A2832] dark:text-white">
                                    {request.title}
                                </h3>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {status.label}
                                </span>
                            </div>
                            <p className="text-sm text-[#7B8C9B] mb-3">
                                {request.description}
                            </p>
                            <div className="flex items-center gap-2 text-[#0D5F6B]">
                                <ThumbsUp className="w-4 h-4" />
                                <span className="text-sm font-medium">{request.votes} votes</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Email CTA */}
            <div className="mt-6 text-center">
                <p className="text-sm text-[#7B8C9B] mb-2">
                    You can also email feature requests directly to:
                </p>
                <a
                    href="mailto:support@way2will.com?subject=[FEATURE REQUEST]"
                    className="text-[#0D5F6B] font-semibold"
                >
                    support@way2will.com
                </a>
            </div>
        </div>
    );
}
